import React, { useState } from 'react';
import { Box, Grid, styled, Typography, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import Kwara from './../images/Community/KwaraBuildGroup.jpg';
import FTN from './../images/Community/ftn.jpeg';
import KBWIT from './../images/Community/kbwomen.jpg';
import EmpowerHer from './../images/Community/empowerher.png';
import GDSC from './../images/Community/gdsc.png';
import HerTech from './../images/Community/hertechafrica.jpeg';
import Newbii from './../images/Community/newbii.png'

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: 'white',
  paddingTop: '140px',
  paddingBottom: '80px',
}));

const ContentWrapper = styled(Box)({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 40px',
  
  '@media (max-width: 768px)': {
    padding: '0 20px',
  },
});

const FilterContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '12px',
  marginBottom: '60px',
  flexWrap: 'wrap',
});

const FilterButton = styled(Button)(({ active }) => ({
  fontFamily: 'Oswald',
  fontSize: '14px',
  letterSpacing: '1px',
  padding: '10px 20px',
  border: '1px solid black',
  backgroundColor: active ? 'black' : 'white',
  color: active ? 'white' : 'black',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    backgroundColor: 'black',
    color: 'white',
  },
}));

const ImpactGrid = styled(Grid)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '40px',
  
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '30px',
  },
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    gap: '30px',
  },
});

const ImpactCard = styled(Box)({
  backgroundColor: 'white',
  border: '1px solid #E0E0E0',
  overflow: 'hidden',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  cursor: 'pointer',
  
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
  },
});

const CardImage = styled('img')({
  width: '100%',
  height: '250px',
  objectFit: 'cover',
});

const CardContent = styled(Box)({
  padding: '24px',
});

const CategoryBadge = styled(Box)(({ category }) => {
  const getColor = () => {
    switch(category) {
      case 'volunteering': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'mentorship': return { bg: '#e3f2fd', color: '#1565c0' };
      default: return { bg: '#f5f5f5', color: '#666' };
    }
  };
  
  const colors = getColor();
  
  return {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: colors.bg,
    color: colors.color,
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  };
});

const CardTitle = styled(Typography)({
  fontFamily: 'Oswald',
  fontSize: '22px',
  fontWeight: 400,
  letterSpacing: '1px',
  marginBottom: '8px',
  lineHeight: '1.3',
});

const OrganizationName = styled(Typography)({
  fontFamily: 'Raleway',
  fontSize: '16px',
  color: '#666',
  marginBottom: '12px',
  fontWeight: 500,
});

const DatePeriod = styled(Typography)({
  fontFamily: 'Raleway',
  fontSize: '14px',
  color: '#999',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const CTAButton = styled(Button)({
  fontFamily: 'Oswald',
  fontSize: '14px',
  letterSpacing: '1px',
  padding: '10px 20px',
  border: '1px solid black',
  backgroundColor: 'transparent',
  color: 'black',
  width: '100%',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    backgroundColor: 'black',
    color: 'white',
  },
});

// Community Impact Component
const CommunityImpact = () => {
  const [filter, setFilter] = useState('all');

  // meta data
  const impactActivities = [
    {
      id: 1,
      name: 'Female Tech Nigeria',
      organization: 'Female Tech Nigeria',
      category: 'volunteering',
      datePeriod: '2020-2021',
      image: FTN,
      ctaText: 'READ More',
      role: 'Founder',
      twitter: 'https://x.com/Femaletechng',
      instagram: 'https://www.instagram.com/femaletechng/',
      description: 'During my undergraduate internship as a Computer Science student, I noticed ratios like 1 woman to 10 men on tech teams, a problem impossible to ignore. I realised that waiting for representation to "fix itself" wasn\'t an option, so alongside five other women, I founded Female Tech Nigeria to encourage more female university students across Nigeria to explore and stay in tech.',
      content: `<h4> What we did </h4>
            <ul> 
            <li> Shared weekly stories of women in tech to make success feel visible and attainable </li>
            <li> Built an active community where members could learn, ask questions, and support one another </li>
            <li> Organised a bootcamp across multiple tracks including web development, product design, and photography </li>
            <li> Hosted regular sessions focused on onboarding and retaining women in tech spaces </li>
            </ul>

            <h4> Impact </h4>
            <ul> 
            <li> Grew a community of over 500 active female members </li>
            <li> Ran a bootcamp with 259 registered participants and a 77% completion rate </li>
            </ul>`
    },
    {
      id: 2,
      name: 'Kwara Build Annual Conference',
      organization: 'Kwara Build',
      category: 'volunteering',
      datePeriod: '2022 - Present',
      linkedin: 'https://www.linkedin.com/company/kwarabuild/',
      image: Kwara,
      ctaText: 'Read More',
      role: 'Core Team Member',
      twitter: 'https://x.com/kwarabuild',
      instagram: 'https://www.instagram.com/kwarabuild/',
      website: 'https://kwarabuild.org/kbtc25',
      description: 'KwaraBuild Tech Conference 2025 was held from November 20–22, under the theme "The Next Builders." The conference brought together startups, investors, builders, tech professionals, and business leaders, with 20+ sessions, 3 panel discussions, and 2 keynotes, reaching over 1,000 attendees. I worked as the Program Manager, with a focus on speaker engagement and volunteer coordination.',
      content: `<h4> My role </h4>
<ul> 
<li> Selected and worked with speakers, curating topics with the audience in mind </li>
<li> Managed speaker welfare, schedules, and session timelines </li>
<li> Coordinated over 20 volunteers, assigning responsibilities and supporting them through execution </li>
</ul>

<h4> Outcome </h4>
<ul>
<li> Positive feedback from both speakers and attendees <a href='https://medium.com/kwara-build/inside-kbtc25-a-2025-word-review-of-the-8th-kwara-build-tech-conference-3c0995ca59d4'> Read here </li>
<li> Media coverage from outlets including <a href='https://www.vanguardngr.com/2025/11/over-2000-tech-fans-storm-ilorin-for-empowerment-conference/'> Vanguard, </a>, <a href='https://www.vanguardngr.com/2025/11/over-2000-tech-fans-storm-ilorin-for-empowerment-conference/'> Daily Source, </a> <a href='https://radiokwara.com/news/technology/kwarabuild-tech-2025-conference-over-2000-participants-plan-to-build-strong-national-tech-ecosystem/'> RadioKwara </a>, among others </li>
<li> Increased community engagement following the event </li>
</ul>`
    },
    {
      id: 3,
      name: 'KwaraBuild WomenInTech',
      organization: 'KwaraBuild',
      category: 'volunteering',
      datePeriod: 'Present',
      linkedin: 'https://www.linkedin.com/company/kwarabuild/',
      image: KBWIT,
      ctaText: 'Check Details',
      role: 'Lead',
      twitter: 'https://x.com/KB_WomenInTech',
      instagram: 'https://www.instagram.com/kbwomenintech/',
      website: 'https://kwarabuild.org/women-in-tech',
      description: 'I currently lead the Women in Tech pillar of the KwaraBuild community, a close-knit group of women committed to growth and shared progress. Our focus is on bringing access to information, global standards, and real opportunities to a local community, while intentionally advancing gender inclusion. We are deliberate about building genuine connections, strong structures, and consistent support. My work centres on championing inclusion by supporting members at different stages of their careers through practical opportunities for learning, mentorship, and visibility. This role is especially meaningful to me, as it allows me to apply the lessons and experience from building Female Tech Nigeria within a broader community context.',
      content: ``
    },
    {
      id: 4,
      name: 'HerTechAfrica Mentorship Program',
      organization: 'HerTech Africa',
      category: 'mentorship',
      datePeriod: 'Aug. 2025 - Oct. 2025',
      image: HerTech,
      ctaText: 'Learn More',
      linkedin: 'ng.linkedin.com/company/her-tech-africa',
      instagram:'https://www.instagram.com/her_tech_africa/',
      twitter: 'https://x.com/HerTechAfrica',
      role: 'Mentor',
      description: 'The 8 weeks program was designed to give mentees direct access to mentors(like myself) who provide real-world guidance on career paths, help clarify challenges, and offer support in any areas mentees bring forward.',
      content: `<h4> The Activities:</h4>
<ul>
  <li>Conducted skill audits for two mentees, identifying gaps based on industry expectations for product management skillsets.</li>
  <li>Reviewed resumes, provided standardized templates, and drafted resumes reflecting their experience and collaborations.</li>
  <li>Identified strengths and guided mentees through interview preparation.</li>
  <li>Shared product case studies for hands-on practice.</li>
  <li>Boosted confidence in transitioning from their current backgrounds.</li>
  <li>Offered clarity in one-on-one sessions tailored to mentees’ interests or career concerns.</li>
</ul>`
    },
    {
      id: 5,
      name: 'EmpowerHer Non-Technical Bootcamp 3.0',
      organization: 'EmpowerHer Community',
      category: 'mentorship',
      datePeriod: 'Jul. 2025 - Sept. 2025',
      image: EmpowerHer,
      ctaText: 'View Details',
      role: 'Mentor',
      twitter: 'https://x.com/empowerhercom',
      instagram: 'https://www.instagram.com/empowerher_community',
      linkedin: 'https://www.linkedin.com/company/empower-her-community/',
      cert: 'https://drive.google.com/file/d/1HWrOrnk_LR3GrSSLY4Ff5aUFmqqwnyUY/view?usp=sharing',
      description: 'A three-month bootcamp focused on providing foundational knowledge in product management, with teachings designed to serve as a reference throughout participants’ careers.',
      content: `<h3>My Activities:</h3>
<ul>
  <li>Developed a beginner-friendly three-month learning curriculum for new product managers. 
      <a href="https://docs.google.com/spreadsheets/d/1qC5Tz0Q9-mkzdXxGAPuILcWNM0EWOZyUGrxKRdrBYyI/edit?usp=sharing" target="_blank">View curriculum</a>
  </li>
  <li>Conducted classes three times a week, engaging 7 participants per session:
    <ul>
      <li>Covered concepts, processes, and tools including problem solving, research, idea generation, prioritization, and more.</li>
      <li>Provided references and examples where necessary.</li>
      <li>Delivered 24 classes/lessons. <a href="https://drive.google.com/drive/folders/1AaLHFqB10JmrSK9wuWwfeiLvEA_KBSDX?usp=sharing target="_blank">Watch all sessions here</a></li>
    </ul>
  </li>
  <li>Provided relevant takeaways to foster a practical learning environment.</li>
  <li>Shared books, articles, and videos that facilitated discussion and were dissected in class.</li>
  <li>Mentored participants on their capstone projects.</li>
</ul>`
    },
    {
      id: 6,
      name: 'Newbii Africa Mentorship Program',
      organization: 'Newbii Africa',
      category: 'mentorship',
      datePeriod: 'March 2025 - Present',
      image: Newbii,
      ctaText: 'Check Details',
      role: 'Mentor',
      linkedin: 'https://www.linkedin.com/company/newbiiafrica/',
      website: 'https://www.newbiiafrica.com/',
      description: `Newbie Africa is a community dedicated to helping newcomers get started in technology. I serve as an 
official Product Management mentor for a community of over 100 members from Ghana, Nigeria, South Africa, and other countries.`,
      content: `<h3>My Activities:</h3>
      As an <a href="https://www.linkedin.com/posts/newbiiafrica_unlock-mentorship-opportunities-activity-7298612519626063873-PEAQ?utm_source=social_share_send&utm_medium=member_desktop_web&rcm=ACoAADEQx-IBSIAD0UhI_ZnLn5GsDFOaapAVhoM" target="_blank"> official mentor </a>, I
<ul>
  <li>Regularly share industry updates and resources with the Product Management community.</li>
  <li>Conduct consecutive clarity sessions based on member demand.</li>
  <li>Answer questions in community channels by providing recommendations, feedback, and guidance.</li>
</ul>
`
    },
    {
      id: 7,
      name: 'GDSC University of Ilorin',
      organization: 'GDSC University of Ilorin',
      category: 'volunteering',
      datePeriod: '2020 - 2022',
      image: GDSC,
      ctaText: 'View Details',
      twitter: 'https://x.com/gdgoc_unilorin',
      instagram: 'https://www.instagram.com/gdgoc_unilorin',
      linkedin: 'https://www.linkedin.com/company/gdg-on-campus-university-of-ilorin',
      cert: 'https://drive.google.com/file/d/1xjRkbvmLSni3atWEJW_W5ANxN2Cea1Zs/view?usp=sharing',
      role: 'Core Team Member',
      description: 'During my time with the Google Developer Student Clubs at the University of Ilorin, I gained my first leadership experience across two academic sessions. I worked closely with the core team and led the technical unit ahead of each academic session, focusing on speaker engagement at a time when participation in digital technology on campus was still developing.',
      content: `<h4> My Contribution </h4>
<ul>
<li> Organized tech workshops and study sessions </li>
<li> Supported community building initiatives </li>
<li> Helped onboard new members into the tech community </li>
</ul>`
    },
  ];

  const filteredActivities = filter === 'all' 
    ? impactActivities 
    : impactActivities.filter(activity => activity.category === filter);

  // const stats = {
  //   organizations: impactActivities.length,
  //   mentees: 50,
  // };

  const getCategoryLabel = (category) => {
    const labels = {
      volunteering: 'Volunteering',
      mentorship: 'Mentorship',
    };
    return labels[category] || category;
  };

  return (
    <PageContainer>
      <ContentWrapper>
        {/* <PageTitle>COMMUNITY IMPACT</PageTitle> */}
        
        {/* <PageDescription> */}
       
        {/* </PageDescription> */}

        {/* Impact Stats */}
        {/* <StatsContainer>
          <StatItem>
            <StatNumber>{stats.organizations}+</StatNumber>
            <StatLabel>Organizations</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>{stats.mentees}+</StatNumber>
            <StatLabel>Mentees</StatLabel>
          </StatItem>
        </StatsContainer> */}
 <Typography sx={{ mb: 4, fontSize: "20px" }}>
          The words that drive me are 
        <b><i> "You can be excellent on your own. You change the world by bringing others with you." </i></b>
         My personal mission is to help support others in their growth while continuing to learn and grow myself even from  the people i help. I do this via mentoring or volunteering as core members of communities to help others grow meaningfully.
       </Typography>
        {/* Filter Buttons */}
        <FilterContainer>
          <FilterButton 
            active={filter === 'all' ? 1 : 0}
            onClick={() => setFilter('all')}
          >
            ALL
          </FilterButton>
          <FilterButton 
            active={filter === 'volunteering' ? 1 : 0}
            onClick={() => setFilter('volunteering')}
          >
            VOLUNTEERING
          </FilterButton>
          <FilterButton 
            active={filter === 'mentorship' ? 1 : 0}
            onClick={() => setFilter('mentorship')}
          >
            MENTORSHIP
          </FilterButton>
        </FilterContainer>

        {/* Impact Cards Grid */}
        <ImpactGrid>
          {filteredActivities.map((activity) => (
            <ImpactCard key={activity.id}>
              <CardImage 
                src={activity.image} 
                alt={activity.name}
              />
              <CardContent>
                <CategoryBadge category={activity.category}>
                  {getCategoryLabel(activity.category)}
                </CategoryBadge>
                
                <CardTitle>{activity.name}</CardTitle>
                
                <OrganizationName>
                  <b>{activity.organization} | {activity.role}</b>
                </OrganizationName>
                
                <DatePeriod>
                  <Icon icon="mdi:calendar-outline" style={{ fontSize: '16px' }} />
                  <b>{activity.datePeriod}</b>
                </DatePeriod>
                
                <Link 
                  to={`/community-details/${activity.id}`}
                  state={{ activity }}
                  style={{ textDecoration: 'none' }}
                >
                  <CTAButton>
                    {activity.ctaText.toUpperCase()}
                  </CTAButton>
                </Link>
              </CardContent>
            </ImpactCard>
          ))}
        </ImpactGrid>
      </ContentWrapper>
    </PageContainer>
  );
};

export default CommunityImpact;
